import { useEffect, useState } from 'react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

/**
 * Custom hook for making columns droppable
 */
export const useDroppableColumn = ({ containerRef, columnId, index, onCardDrop, onColumnReorder, isLocked = false }) => {
  const [dropState, setDropState] = useState({
    isDraggingOver: false,
    edge: null,
    type: null,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const cleanup = dropTargetForElements({
      element: container,
      canDrop: ({ source }) => {
        if (source.data.type === 'card') return true;
        // Prevent column reordering if this column is locked
        if (source.data.type === 'column' && source.data.columnId !== columnId && !isLocked) return true;
        return false;
      },
      getData: ({ input, source }) => {
        const baseData = { columnId, index };

        if (source.data.type === 'column') {
          return attachClosestEdge(baseData, {
            input,
            element: container,
            allowedEdges: ['left', 'right'],
          });
        }

        return baseData;
      },
      onDragEnter: ({ source, self }) => {
        setDropState({
          isDraggingOver: true,
          edge: extractClosestEdge(self.data),
          type: source.data.type,
        });
      },
      onDrag: ({ source, self }) => {
        setDropState({
          isDraggingOver: true,
          edge: extractClosestEdge(self.data),
          type: source.data.type,
        });
      },
      onDragLeave: () => {
        setDropState({
          isDraggingOver: false,
          edge: null,
          type: null,
        });
      },
      onDrop: ({ source, self }) => {
        setDropState({
          isDraggingOver: false,
          edge: null,
          type: null,
        });

        if (source.data.type === 'card') {
          onCardDrop({
            sourceCardId: source.data.cardId,
            sourceColumnId: source.data.columnId,
            targetColumnId: columnId,
            position: 'end',
            leadItem: source?.data?.leadItem, // Pass lead item from drag data
          });
        } else if (source.data.type === 'column') {
          const edge = extractClosestEdge(self.data);
          onColumnReorder({
            sourceIndex: source.data.index,
            targetIndex: index,
            edge,
          });
        }
      },
    });

    return cleanup;
  }, [containerRef, columnId, index, onCardDrop, onColumnReorder, isLocked]);

  return dropState;
};
